import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, input } from '@angular/core';
import {
  AxialLengthType,
  EyeSideValues,
  GraphDataEthnicity,
  MyopeProgressionType,
  SERValueType,
} from '../backend-response-models/calc-enums.helper';
import { EyeSideDropDown } from '../shared/shared-enums/enum-helper';
import { MyopeProgressionStat } from '../backend-response-models/calc-interfaces.helper';
import { TranslatePipe } from '../languages/translate.pipe';
import { COMMON_IMPORTS } from '../common-imports';
import { ChartModule } from 'primeng/chart';
import { DropdownModule } from 'primeng/dropdown';
import { PanelModule } from 'primeng/panel';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { ComparativeAxialDataComponent } from './comparative-axial-data/comparative-axial-data.component';
import { ComparativeSerDataComponent } from './comparative-ser-data/comparative-ser-data.component';
import { ReferencesComponent } from '../shared/references/references.component';

@Component({
  selector: 'app-standard-progression',
  standalone: true,
  imports: [
    ...COMMON_IMPORTS,
    PanelModule,
    DropdownModule,
    ChartModule,
    ChipModule,
    DialogModule,
    TooltipModule,
    AccordionModule,
    ComparativeAxialDataComponent,
    ComparativeSerDataComponent,
    ReferencesComponent,
  ],
  templateUrl: './standard-progression.component.html',
  styleUrl: './standard-progression.component.css',
})
export class StandardProgressionComponent implements OnChanges, OnInit {
  MyopeProgressionType = MyopeProgressionType;

  @Input({ required: true })
  mostMyopicEyeSide!: EyeSideValues;

  @Input({ required: true })
  ageAtProgression?: number;

  @Input({ required: true })
  leftEyeProgressionStat?: MyopeProgressionStat;

  @Input({ required: true })
  rightEyeProgressionStat?: MyopeProgressionStat;

  @Input()
  personalization?: string;

  @Output()
  ethnicityChanged = new EventEmitter<GraphDataEthnicity>();

  @Input({ required: true })
  ethnicity?: GraphDataEthnicity;

  public eyeSideDropDownChoices: EyeSideDropDown[] = [];
  public eyeSideSelected?: EyeSideDropDown;
  public progressionTypeLabel: string = 'NONE';
  public explanationLabel: string = 'NONE';
  public serLabel: string = 'NONE';
  public alLabel: string = 'NONE';
  public ageLabel: string = 'NONE';
  public ageAtLatestVisitLabel?: string = 'NONE';
  public ageAtComparisonVisitLabel?: string = 'NONE';
  public selectedEyeProgressionStat?: MyopeProgressionStat;
  public showNonCycloWarning = false;
  public showKValueWarning = false;
  public showExtrapolationWarning = false;
  public showComparativeData = false;
  public showReferencesDialog = false;

  constructor(private readonly translatePipe: TranslatePipe) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {
    this.refreshAll();
  }

  ngOnInit(): void {
    this.fillTranslations();
    this.setToMyopicEye();
    this.refreshAll();
  }

  private setToMyopicEye() {
    const startingEyeSide = this.eyeSideDropDownChoices.find(e => e.value === this.mostMyopicEyeSide);
    this.eyeSideSelected = startingEyeSide;
    const isLeftEye = this.eyeSideSelected?.value === EyeSideValues.LEFT;
    this.selectedEyeProgressionStat = isLeftEye ? this.leftEyeProgressionStat : this.rightEyeProgressionStat;
  }

  private fillTranslations() {
    const leftEye = this.translatePipe.transform('LEFT_EYE');
    const leftEyeMostMyopic = this.translatePipe.transform('LEFT_EYE_MOST_MYOPIC');
    const rightEye = this.translatePipe.transform('RIGHT_EYE');
    const rightEyeMostMyopic = this.translatePipe.transform('RIGHT_EYE_MOST_MYOPIC');

    this.eyeSideDropDownChoices = [
      {
        label: this.mostMyopicEyeSide == EyeSideValues.LEFT ? leftEyeMostMyopic : leftEye,
        value: EyeSideValues.LEFT,
      },
      {
        label: this.mostMyopicEyeSide == EyeSideValues.RIGHT ? rightEyeMostMyopic : rightEye,
        value: EyeSideValues.RIGHT,
      },
    ];
  }

  ethnicityFromCompartiveDidChange(event: GraphDataEthnicity) {
    this.ethnicity = event;
    this.ethnicityChanged.emit(event);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEyeSideChange(event: any): void {
    console.log('Selected Eye Side:', event.value);
    this.eyeSideSelected = event.value;
    this.fillTranslations();
    this.refreshAll();
  }

  refreshAll() {
    const isLeftEye = this.eyeSideSelected?.value === EyeSideValues.LEFT;
    this.selectedEyeProgressionStat = isLeftEye ? this.leftEyeProgressionStat : this.rightEyeProgressionStat;

    //show age
    const age = this.ageAtProgression ?? 0;
    const ageAtLatestVisit = this.selectedEyeProgressionStat?.ageAtLatestVisit;
    const ageAtComparisonVisit = this.selectedEyeProgressionStat?.ageAtComparisonVisit;

    if (ageAtLatestVisit !== undefined || ageAtComparisonVisit !== undefined) {
      this.ageAtLatestVisitLabel = ageAtLatestVisit!.toFixed(2) + ' ' + this.translatePipe.transform('y.o');
      this.ageAtComparisonVisitLabel = ageAtComparisonVisit!.toFixed(2) + ' ' + this.translatePipe.transform('y.o');
    } else {
      this.ageAtLatestVisitLabel = undefined;
      this.ageAtComparisonVisitLabel = undefined;
    }

    this.ageLabel = age.toFixed(2) + ' ' + this.translatePipe.transform('y.o');

    //show warnings
    const serType = this.selectedEyeProgressionStat?.serType;
    if (serType) {
      this.showNonCycloWarning = serType === SERValueType.NON_CYCLO;
    } else {
      this.showNonCycloWarning = false;
    }

    const axialLengthType = this.selectedEyeProgressionStat?.axialLengthType;
    if (axialLengthType) {
      this.showKValueWarning = axialLengthType === AxialLengthType.ESTIMATED;
    } else {
      this.showKValueWarning = false;
    }

    //If we have a type, we can fill the rest of the labels
    const type = this.selectedEyeProgressionStat?.myopeProgressionType;
    if (type) {
      const typeTranslationKey = type === MyopeProgressionType.REAL ? 'CALCULATED' : 'EXTRAPOLATED';
      this.progressionTypeLabel = this.translatePipe.transform(typeTranslationKey);
      const explanationTranslationKey =
        type === MyopeProgressionType.REAL ? 'MYOPIA_PROGRESSION_EXPLAINATION_MEASURED' : 'MYOPIA_PROGRESSION_EXPLAINATION_PROJECTED';
      this.explanationLabel = this.translatePipe.transform(explanationTranslationKey);
      this.showExtrapolationWarning = type === MyopeProgressionType.ESTIMATED;
    } else {
      this.progressionTypeLabel = '----';
      this.explanationLabel = '----';
    }

    const year = this.translatePipe.transform('YEAR');
    const hasSer = this.selectedEyeProgressionStat?.serPerYear !== undefined && this.selectedEyeProgressionStat?.serPerYear !== null;

    if (hasSer) {
      this.serLabel = this.selectedEyeProgressionStat!.serPerYear!.toFixed(2) + `D / ${year}`;
    } else {
      this.serLabel = '----';
    }

    const hasAxialLength =
      this.selectedEyeProgressionStat?.axialLengthPerYear !== undefined && this.selectedEyeProgressionStat?.axialLengthPerYear !== null;

    if (hasAxialLength) {
      this.alLabel = (this.selectedEyeProgressionStat!.axialLengthPerYear ?? 0).toFixed(2) + `mm / ${year}`;
    } else {
      this.alLabel = '----';
    }
  }

  showReferences() {
    this.showReferencesDialog = true;
  }

  hideReferences() {
    this.showReferencesDialog = false;
  }
}
